// src/components/circles/SmartRulesBuilder.tsx
//
// Rule builder for Smart Circles. A Smart Circle's members are the contacts
// that match EVERY rule — evaluated by the server each time, and only ever
// the contacts the viewer is allowed to see. Nothing here stores members.
//
// Each rule is: field → condition → typed value. No free-text expressions.
// Values are always chosen or typed into a typed input (tags, a person, a
// status set, a date, a number of days, a short piece of text); raw ids are
// never shown — people and companies appear by name.
//
// Live preview: debounced POST /api/circles/smart-preview with the rules,
// once every rule is complete. The count and names it shows are the viewer's
// own view (record-scoped server-side).

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { usePreviewSmartCircleMutation } from '../../api/circlesApi';
import { useGetCompaniesQuery } from '../../api/companiesApi';
import type {
  ContactFilterError,
  ContactFilterField,
  SmartCirclePreview,
} from '../../types/api';
import type { CircleOwnerCandidate, OwnerCandidatesStatus } from './CircleFormFields';
import {
  CONTACT_TYPE_LABELS,
  FIELD_META,
  FIELD_ORDER,
  RELATIONSHIP_STATUS_LABELS,
  draftsToFilter,
  errorsByRule,
  incompleteRuleErrors,
  newDraft,
  opMeta,
  withField,
  withOp,
  type SmartRuleDraft,
} from './smartRules';

export const SMART_PREVIEW_DEBOUNCE_MS = 400;
export const MAX_SMART_RULES = 20;

interface SmartRulesBuilderProps {
  drafts: SmartRuleDraft[];
  onChange: (drafts: SmartRuleDraft[]) => void;
  /** Field-level errors to show (from a refused save, or a submit attempt). */
  errors?: ContactFilterError[];
  members?: CircleOwnerCandidate[];
  membersStatus?: OwnerCandidatesStatus;
  currentUserId?: string | null;
  disabled?: boolean;
  /** Turn off the live preview (e.g. tests of the editor alone). */
  preview?: boolean;
}

const inputCls =
  'block w-full min-w-0 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/40 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 aria-[invalid=true]:border-red-400';
const labelCls = 'block text-[11px] font-medium text-slate-700 mb-1';

export default function SmartRulesBuilder({
  drafts,
  onChange,
  errors,
  members = [],
  membersStatus = 'ready',
  currentUserId = null,
  disabled,
  preview = true,
}: SmartRulesBuilderProps) {
  const baseId = useId();
  const [previewState, setPreviewState] = useState<
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ok'; data: SmartCirclePreview }
    | { status: 'invalid'; errors: ContactFilterError[] }
    | { status: 'error'; message: string }
  >({ status: 'idle' });
  const [runPreview] = usePreviewSmartCircleMutation();
  // Held in a ref so the debounce effect depends only on the rules.
  const runPreviewRef = useRef(runPreview);
  runPreviewRef.current = runPreview;

  const usesCompany = drafts.some((d) => d.field === 'companyId');
  const { data: companiesData, isLoading: companiesLoading } = useGetCompaniesQuery(
    { limit: 200 },
    { skip: !usesCompany },
  );
  const companies = useMemo(
    () => [...(companiesData?.companies ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [companiesData],
  );

  const memberOptions = useMemo(
    () =>
      [...members]
        .map((m) => ({
          id: m.id,
          label:
            (m.fullName?.trim() || m.email?.trim() || 'Unnamed member') +
            (currentUserId && m.id === currentUserId ? ' (you)' : ''),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [members, currentUserId],
  );

  // ---- Live preview (debounced, only once every rule is complete) --------
  const filterKey = JSON.stringify(draftsToFilter(drafts));
  const complete = drafts.length > 0 && incompleteRuleErrors(drafts).length === 0;
  const requestSeq = useRef(0);
  useEffect(() => {
    if (!preview) return;
    if (!complete) {
      setPreviewState({ status: 'idle' });
      return;
    }
    const seq = ++requestSeq.current;
    setPreviewState({ status: 'loading' });
    const t = setTimeout(async () => {
      try {
        const data = await runPreviewRef.current({ rules: JSON.parse(filterKey) }).unwrap();
        if (seq === requestSeq.current) setPreviewState({ status: 'ok', data });
      } catch (err: unknown) {
        if (seq !== requestSeq.current) return;
        const e = err as { status?: number; data?: { code?: string; errors?: ContactFilterError[]; error?: string } };
        if (e?.data?.code === 'INVALID_SMART_RULES' && Array.isArray(e.data.errors)) {
          setPreviewState({ status: 'invalid', errors: e.data.errors });
        } else if (e?.status === 403) {
          setPreviewState({ status: 'error', message: 'You can’t preview Smart Circle rules.' });
        } else {
          setPreviewState({ status: 'error', message: 'Preview unavailable right now.' });
        }
      }
    }, SMART_PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filterKey, complete, preview]);

  const shownErrors = useMemo(() => {
    const fromPreview = previewState.status === 'invalid' ? previewState.errors : [];
    return errorsByRule([...(errors ?? []), ...fromPreview]);
  }, [errors, previewState]);
  const topLevelErrors = shownErrors.get(-1) ?? [];

  const update = (i: number, next: SmartRuleDraft) => onChange(drafts.map((d, j) => (j === i ? next : d)));
  const remove = (i: number) => onChange(drafts.filter((_, j) => j !== i));
  const add = () => onChange([...drafts, newDraft()]);

  return (
    <fieldset className="min-w-0 space-y-2" data-testid="smart-rules-builder" disabled={disabled}>
      <legend className="text-[11px] font-medium text-slate-700">Rules</legend>
      <p id={`${baseId}-help`} className="text-[11px] text-slate-500">
        Contacts that match <strong>all</strong> of these rules are members, and membership updates
        by itself as contacts change. Everyone sees only the matching contacts they already have
        access to.
      </p>

      {drafts.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 px-3 py-2 text-[11px] text-slate-600">
          No rules yet — add a rule to choose who belongs.
        </p>
      )}

      <ol className="space-y-2">
        {drafts.map((d, i) => (
          <RuleRow
            key={d.key}
            index={i}
            draft={d}
            baseId={`${baseId}-${d.key}`}
            errors={shownErrors.get(i) ?? []}
            memberOptions={memberOptions}
            membersStatus={membersStatus}
            companies={companies}
            companiesLoading={companiesLoading}
            onChange={(next) => update(i, next)}
            onRemove={() => remove(i)}
          />
        ))}
      </ol>

      {topLevelErrors.length > 0 && (
        <p role="alert" className="text-[11px] text-red-700">
          {topLevelErrors.map((e) => e.message).join(' ')}
        </p>
      )}

      <button
        type="button"
        onClick={add}
        disabled={disabled || drafts.length >= MAX_SMART_RULES}
        className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
        data-testid="smart-rules-add"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden /> Add rule
      </button>

      {preview && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-md bg-slate-50 px-3 py-2 text-[11px] text-slate-700"
          data-testid="smart-rules-preview"
        >
          {previewState.status === 'idle' &&
            (drafts.length === 0 ? 'Add a rule to see who matches.' : 'Finish each rule to see who matches.')}
          {previewState.status === 'loading' && 'Checking who matches…'}
          {previewState.status === 'invalid' && 'Fix the highlighted rules to see who matches.'}
          {previewState.status === 'error' && previewState.message}
          {previewState.status === 'ok' && (
            <>
              <span className="font-medium">
                {previewState.data.count === 1
                  ? '1 matching contact'
                  : `${previewState.data.count} matching contacts`}
              </span>{' '}
              you can see
              {previewState.data.sample.length > 0 && (
                <>
                  {': '}
                  <span className="break-words">
                    {previewState.data.sample.map((s) => s.displayName).join(', ')}
                    {previewState.data.count > previewState.data.sample.length ? ', …' : ''}
                  </span>
                </>
              )}
            </>
          )}
        </div>
      )}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------

function RuleRow({
  index,
  draft,
  baseId,
  errors,
  memberOptions,
  membersStatus,
  companies,
  companiesLoading,
  onChange,
  onRemove,
}: {
  index: number;
  draft: SmartRuleDraft;
  baseId: string;
  errors: ContactFilterError[];
  memberOptions: Array<{ id: string; label: string }>;
  membersStatus: OwnerCandidatesStatus;
  companies: Array<{ id: string; name: string }>;
  companiesLoading: boolean;
  onChange: (d: SmartRuleDraft) => void;
  onRemove: () => void;
}) {
  const n = index + 1;
  const meta = draft.field ? FIELD_META[draft.field] : undefined;
  const kind = opMeta(draft.field, draft.op)?.kind;
  const errFor = (part: 'field' | 'op' | 'value') =>
    errors.filter((e) => e.path === `all[${index}].${part}` || e.path.startsWith(`all[${index}].${part}[`));
  const ruleLevel = errors.filter((e) => e.path === `all[${index}]` || /^all\[\d+\]\.(?!field|op|value)/.test(e.path));
  const fieldErr = errFor('field');
  const opErr = errFor('op');
  const valueErr = errFor('value');
  const errId = (part: string) => `${baseId}-${part}-err`;

  return (
    <li className="min-w-0 rounded-md border border-slate-200 p-2" data-testid={`smart-rule-${index}`}>
      <fieldset className="relative min-w-0">
        <legend className="mb-1 text-[11px] font-semibold text-slate-600">Rule {n}</legend>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove rule ${n}`}
          className="absolute right-0 top-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>

        <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="min-w-0">
            <label htmlFor={`${baseId}-field`} className={labelCls}>
              Match on
            </label>
            <select
              id={`${baseId}-field`}
              value={draft.field}
              onChange={(e) => onChange(withField(draft, e.target.value as ContactFilterField | ''))}
              className={inputCls}
              aria-invalid={fieldErr.length > 0}
              aria-describedby={fieldErr.length > 0 ? errId('field') : undefined}
            >
              <option value="">Choose a field…</option>
              {FIELD_ORDER.map((f) => (
                <option key={f} value={f}>
                  {FIELD_META[f].label}
                </option>
              ))}
            </select>
            <ErrorText id={errId('field')} errors={fieldErr} />
          </div>

          {meta && (
            <div className="min-w-0">
              <label htmlFor={`${baseId}-op`} className={labelCls}>
                Condition
              </label>
              <select
                id={`${baseId}-op`}
                value={draft.op}
                onChange={(e) => onChange(withOp(draft, e.target.value))}
                className={inputCls}
                aria-invalid={opErr.length > 0}
                aria-describedby={opErr.length > 0 ? errId('op') : undefined}
              >
                {meta.ops.map((o) => (
                  <option key={o.op} value={o.op}>
                    {o.label}
                  </option>
                ))}
              </select>
              <ErrorText id={errId('op')} errors={opErr} />
            </div>
          )}
        </div>

        {meta && kind && kind !== 'none' && (
          <div className="mt-2 min-w-0">
            <ValueInput
              kind={kind}
              id={`${baseId}-value`}
              fieldLabel={meta.label}
              draft={draft}
              invalid={valueErr.length > 0}
              describedBy={valueErr.length > 0 ? errId('value') : undefined}
              memberOptions={memberOptions}
              membersStatus={membersStatus}
              companies={companies}
              companiesLoading={companiesLoading}
              onChange={(value) => onChange({ ...draft, value })}
            />
            <ErrorText id={errId('value')} errors={valueErr} />
          </div>
        )}
        <ErrorText id={errId('rule')} errors={ruleLevel} />
      </fieldset>
    </li>
  );
}

function ErrorText({ id, errors }: { id: string; errors: ContactFilterError[] }) {
  if (errors.length === 0) return null;
  return (
    <p id={id} className="mt-1 text-[11px] text-red-700">
      {[...new Set(errors.map((e) => e.message))].join(' ')}
    </p>
  );
}

function ValueInput({
  kind,
  id,
  fieldLabel,
  draft,
  invalid,
  describedBy,
  memberOptions,
  membersStatus,
  companies,
  companiesLoading,
  onChange,
}: {
  kind: string;
  id: string;
  fieldLabel: string;
  draft: SmartRuleDraft;
  invalid: boolean;
  describedBy?: string;
  memberOptions: Array<{ id: string; label: string }>;
  membersStatus: OwnerCandidatesStatus;
  companies: Array<{ id: string; name: string }>;
  companiesLoading: boolean;
  onChange: (v: string | string[]) => void;
}) {
  const text = typeof draft.value === 'string' ? draft.value : '';
  const common = {
    id,
    className: inputCls,
    'aria-invalid': invalid,
    'aria-describedby': describedBy,
  };

  if (kind === 'statusList' || kind === 'typeList') {
    const labels = kind === 'statusList' ? RELATIONSHIP_STATUS_LABELS : CONTACT_TYPE_LABELS;
    const chosen = Array.isArray(draft.value) ? draft.value : [];
    return (
      <fieldset
        className="min-w-0"
        aria-invalid={invalid}
        aria-describedby={describedBy}
        data-testid={`${id}-choices`}
      >
        <legend className={labelCls}>{fieldLabel}</legend>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {Object.entries(labels).map(([value, label]) => (
            <label key={value} className="inline-flex items-center gap-1 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={chosen.includes(value)}
                onChange={(e) =>
                  onChange(e.target.checked ? [...chosen, value] : chosen.filter((c) => c !== value))
                }
                className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  if (kind === 'memberId') {
    const known = memberOptions.some((m) => m.id === text);
    return (
      <>
        <label htmlFor={id} className={labelCls}>
          Person
        </label>
        <select {...common} value={text} onChange={(e) => onChange(e.target.value)} disabled={membersStatus !== 'ready' && !text}>
          <option value="">
            {membersStatus === 'loading'
              ? 'Loading members…'
              : membersStatus === 'unavailable'
                ? 'Member list unavailable'
                : 'Choose a person…'}
          </option>
          {text && !known && <option value={text}>Current person (not in the member list)</option>}
          {memberOptions.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </>
    );
  }

  if (kind === 'companyId') {
    const known = companies.some((c) => c.id === text);
    return (
      <>
        <label htmlFor={id} className={labelCls}>
          Company
        </label>
        <select {...common} value={text} onChange={(e) => onChange(e.target.value)}>
          <option value="">{companiesLoading ? 'Loading companies…' : 'Choose a company…'}</option>
          {text && !known && !companiesLoading && <option value={text}>A company you can’t view</option>}
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </>
    );
  }

  if (kind === 'tagList') {
    return (
      <>
        <label htmlFor={id} className={labelCls}>
          Tags <span className="font-normal text-slate-400">— separate with commas</span>
        </label>
        <input {...common} type="text" value={text} onChange={(e) => onChange(e.target.value)} placeholder="vip, newsletter" />
      </>
    );
  }

  if (kind === 'date') {
    return (
      <>
        <label htmlFor={id} className={labelCls}>
          Date
        </label>
        <input {...common} type="date" value={text} onChange={(e) => onChange(e.target.value)} />
      </>
    );
  }

  if (kind === 'days') {
    return (
      <>
        <label htmlFor={id} className={labelCls}>
          Number of days
        </label>
        <input
          {...common}
          type="number"
          inputMode="numeric"
          min={1}
          max={3650}
          step={1}
          value={text}
          onChange={(e) => onChange(e.target.value)}
          placeholder="30"
        />
      </>
    );
  }

  return (
    <>
      <label htmlFor={id} className={labelCls}>
        {fieldLabel}
      </label>
      <input {...common} type="text" maxLength={100} value={text} onChange={(e) => onChange(e.target.value)} />
    </>
  );
}
